import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { StoryImportReviewGroup } from "@/features/story-import/story-import.types";
import { ImportReviewCard } from "@/features/story-import/review/ImportReviewCard";

interface ImportReviewSectionProps {
  busyElementIds: string[];
  group: StoryImportReviewGroup;
  onAccept: (elementId: string) => void;
  onBulkAccept: () => void;
  onDiscard: (elementId: string) => void;
  onEdit: (elementId: string) => void;
}

export function ImportReviewSection({
  busyElementIds,
  group,
  onAccept,
  onBulkAccept,
  onDiscard,
  onEdit,
}: ImportReviewSectionProps) {
  const { t } = useTranslation();

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 rounded-[1.5rem] border border-ink/10 bg-white/80 px-5 py-4 shadow-vellum md:flex-row md:items-end md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold text-foreground">{t(group.titleKey)}</h2>
          <p className="text-sm leading-6 text-muted-foreground">{t(group.descriptionKey)}</p>
          {group.count > 0 ? (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Badge variant="outline">
                {t("storyImport.review.pendingCount", {
                  count: group.pendingCount,
                })}
              </Badge>
              <Badge variant="outline">
                {t("storyImport.review.needsReviewCount", {
                  count: group.needsReviewCount,
                })}
              </Badge>
              <Badge variant="outline">
                {t("storyImport.review.lowConfidenceCount", {
                  count: group.lowConfidenceCount,
                })}
              </Badge>
            </div>
          ) : null}
          {group.needsReviewCount > 0 || group.lowConfidenceCount > 0 ? (
            <p className="pt-1 text-xs font-medium uppercase tracking-[0.16em] text-clay">
              {t("storyImport.review.orderingHint")}
            </p>
          ) : null}
        </div>
        {group.bulkAcceptElementType && group.pendingCount > 0 ? (
          <Button onClick={onBulkAccept} type="button" variant="outline">
            {t("storyImport.actions.bulkAccept")}
          </Button>
        ) : null}
      </div>

      {group.items.length === 0 ? (
        <div className="rounded-[1.5rem] border border-dashed border-ink/15 bg-canvas/70 px-5 py-6 text-sm text-muted-foreground">
          {t(group.emptyMessageKey)}
        </div>
      ) : (
        <div className="space-y-3">
          {group.items.map((element) => (
            <ImportReviewCard
              element={element}
              isBusy={busyElementIds.includes(element.id)}
              key={element.id}
              onAccept={() => onAccept(element.id)}
              onDiscard={() => onDiscard(element.id)}
              onEdit={() => onEdit(element.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
